import type { QuantityValue } from '@/types/crm';

// Currency symbol → ISO 4217 code, ambiguous-symbol set, and ISO code pattern
// are ported from the backend's authoritative parser at
// apps/crm_events/services/property_values/field_types/currency.py. This is a
// deliberate best-effort en-US/ISO subset for the CSV mapping-step PREVIEW
// only — it does not aim for full `price-parser` parity. The backend parser
// remains authoritative at import time; a future worker should not chase
// parity here, nor treat a preview `—` (unparseable value) as a bug.
const SYMBOL_TO_ISO: Record<string, string> = {
  $: 'USD',
  '€': 'EUR',
  '£': 'GBP',
  '¥': 'JPY',
  '₹': 'INR',
  '₩': 'KRW',
  '₽': 'RUB',
  R$: 'BRL',
  C$: 'CAD',
  A$: 'AUD',
  NZ$: 'NZD',
  HK$: 'HKD',
  S$: 'SGD',
  Fr: 'CHF',
  zł: 'PLN',
  Kč: 'CZK',
};

const SORTED_SYMBOLS = Object.keys(SYMBOL_TO_ISO).sort(
  (a, b) => b.length - a.length
);

const AMBIGUOUS_SYMBOLS = new Set(['kr']);

const AMBIGUOUS_SYMBOL_RE = new RegExp(
  `(^|\\s)(${[...AMBIGUOUS_SYMBOLS].join('|')})(\\s|$)`,
  'i'
);

const ISO_CODE_RE = /^[A-Z]{3,}$/;

// The authoritative set of currency units Ahoy actually accepts. The
// `^[A-Z]{3,}$` shape is NOT the real gate: at import time the CRM's currency
// field type validates every unit against the CURRENCY_CHOICES-derived unit set
// (`QuantityValueType.validate` in
// apps/crm_events/services/property_values/value_types/quantity.py, seeded from
// apps.crm_events.core.currency.CURRENCY_CHOICES). A code like `ABC` or `USDT`
// satisfies the ISO-shape regex but is rejected there, so a preview that showed
// it as a parsed value (e.g. "50 ABC") would confidently render a value the
// import will refuse. Gating on membership makes the preview show `—` for such
// values instead — the same "no usable value" meaning the dash carries
// everywhere else. Mirrors apps/lightfield_import/services/currency.py's
// SUPPORTED_CURRENCY_CODES; keep both in lockstep with CURRENCY_CHOICES.
const SUPPORTED_CURRENCY_CODES = new Set([
  'USD', 'EUR', 'GBP', 'AUD', 'BRL', 'CAD', 'CHF', 'CNY', 'CZK',
  'DKK', 'HKD', 'HUF', 'IDR', 'ILS', 'INR', 'ISK', 'JPY', 'KRW', 'MXN',
  'MYR', 'NOK', 'NZD', 'PHP', 'PLN', 'RON', 'SEK', 'SGD', 'THB', 'TRY',
  'ZAR',
]); // prettier-ignore

const EN_US_NUMBER_RE = /^-?(\d{1,3}(,\d{3})+|\d+)(\.\d+)?$/;

// When an ISO code is present the amount may still carry a currency symbol
// (e.g. "$5,000 USD"). Strip a symbol whose ISO code MATCHES the unit so the
// numeric token validates. A symbol that maps to a DIFFERENT code (e.g.
// "$5,000 EUR") is left in place, so the conflicting value stays unparseable
// and the preview renders "—" rather than guessing which currency is correct.
function stripMatchingSymbol(remainder: string, unit: string): string {
  for (const symbol of SORTED_SYMBOLS) {
    if (SYMBOL_TO_ISO[symbol] === unit && remainder.includes(symbol)) {
      return remainder.replace(symbol, '');
    }
  }
  return remainder;
}

// Collapse a plain decimal string (`-?\d+(\.\d+)?`) to a canonical form — no
// sign on zero, no leading integer zeros, no trailing fractional zeros — so two
// spellings of the same value ("007" / "7", "1.50" / "1.5", "-0" / "0") compare
// equal.
function canonicalizeDecimal(value: string): string {
  const negative = value.startsWith('-');
  const unsigned = negative ? value.slice(1) : value;
  const [rawInt, rawFrac = ''] = unsigned.split('.');
  const intPart = rawInt.replace(/^0+(?=\d)/, '');
  const fracPart = rawFrac.replace(/0+$/, '');
  const magnitude = fracPart ? `${intPart}.${fracPart}` : intPart;
  if (magnitude === '0') {
    return '0';
  }
  return negative ? `-${magnitude}` : magnitude;
}

// True only when parsing `token` into an IEEE-754 double preserved every digit.
// `Number(...).toString()` yields the shortest decimal that round-trips to the
// same double, so if it differs from the token's canonical form, precision was
// lost during parsing (e.g. "9007199254740993" → 9007199254740992, or
// "9007199254740991.4" → 9007199254740991 with the ".4" dropped). A magnitude
// check alone misses the fractional case because the rounded result can still
// land at or below MAX_SAFE_INTEGER. A number large or small enough to serialize
// in exponential notation is far beyond any real currency amount and is treated
// as unrepresentable.
function reproducesExactly(token: string, parsed: number): boolean {
  const serialized = parsed.toString();
  if (serialized.includes('e') || serialized.includes('E')) {
    return false;
  }
  return canonicalizeDecimal(serialized) === canonicalizeDecimal(token);
}

function parseCurrencyString(raw: string): QuantityValue | null {
  const text = raw.trim();
  if (!text) {
    return null;
  }

  // Refuse ambiguous "kr" (shared by SEK/NOK/DKK/ISK) rather than guessing.
  if (AMBIGUOUS_SYMBOL_RE.test(text)) {
    return null;
  }

  let unit: string | undefined;
  let remainder = text;

  const isoMatch = text.match(/\b[A-Z]{3,}\b/);
  if (isoMatch) {
    unit = isoMatch[0];
    remainder = remainder.replace(unit, '');
    remainder = stripMatchingSymbol(remainder, unit);
  } else {
    for (const symbol of SORTED_SYMBOLS) {
      if (text.includes(symbol)) {
        unit = SYMBOL_TO_ISO[symbol];
        remainder = remainder.replace(symbol, '');
        break;
      }
    }
  }

  // The unit must both look like an ISO code AND be one Ahoy actually accepts.
  // An ISO-shaped but unsupported code (e.g. "ABC") is left unparseable so the
  // preview shows `—` rather than a value the import would reject.
  if (!unit || !ISO_CODE_RE.test(unit) || !SUPPORTED_CURRENCY_CODES.has(unit)) {
    return null;
  }

  const numToken = remainder.replace(/\s/g, '');
  if (!EN_US_NUMBER_RE.test(numToken)) {
    return null;
  }

  const cleaned = numToken.replace(/,/g, '');
  const amount = Number(cleaned);
  // `Number()` silently rounds any token IEEE-754 doubles cannot represent
  // exactly. That loses integer precision above 2^53 ("9007199254740993" becomes
  // 9007199254740992) AND fractional precision at large magnitudes
  // ("9007199254740991.4" becomes 9007199254740991, dropping the ".4" while
  // staying at MAX_SAFE_INTEGER) — so a magnitude check alone lets
  // precision-losing values through. Reject unless the parsed number reproduces
  // the source token exactly, so the preview shows `—` (the same "no usable
  // value" dash carried everywhere else) rather than an amount that differs from
  // both the CSV and the value the backend imports.
  if (!Number.isFinite(amount) || !reproducesExactly(cleaned, amount)) {
    return null;
  }

  // `Number('-0')` yields negative zero, which renders as a misleading
  // "-$0.00" in the preview. Collapse it to positive zero so any zero amount
  // shows without a sign (`amount === 0` matches both 0 and -0).
  const normalizedAmount = amount === 0 ? 0 : amount;

  return { amount: normalizedAmount, unit };
}

export function normalizePreviewValue(
  rawValue: string,
  fieldTypeName: string | undefined
): unknown {
  switch (fieldTypeName) {
    case 'currency':
      return parseCurrencyString(rawValue) ?? rawValue;
    case 'multi_currency': {
      const q = parseCurrencyString(rawValue);
      return q ? [q] : rawValue;
    }
    default:
      return rawValue;
  }
}
